import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, finalize, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { sortBy } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { CombinedConversationState } from '@nimbus-library/features/shared/models/enum/conversation-status.enum';
import { UserAssignmentType } from '@nimbus-global/features/shared/models/domain/user-assignment-type.enum';
import { MemberService } from '@nimbus-global/features/shared/services/member.service';
import { ReportingServiceSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-service-sessions.service';
import { IUserMembership } from '@nimbus-portal/features/user-header/models/user-header-state.model';
import { UserHeaderStateService } from '@nimbus-portal/features/user-header/services/user-header-state.service';
import { UserTeamComponent } from '@nimbus-portal/features/user-header/components/user-team/user-team.component';
import { UserTeamVariant } from '@nimbus-portal/features/user-header/components/user-team/user-team-variant.enum';
import { ResponsibilityProfileService } from '@nimbus-library/features/user-state/services/responsibility-profile.service';
import { IUserResponsibilityProfile } from '@nimbus-library/features/user-state/models/user-responsibility-profile.model';
import { HeaderBarNavigationService } from '@nimbus-portal/common/services/header-bar-navigation.service';
import { HeaderBarPinnedServicesService } from '@nimbus-portal/common/services/header-bar-pinned-services.service';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-header-bar-services',
  templateUrl: './header-bar-services.component.html',
  styleUrl: './header-bar-services.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, ReactiveFormsModule, OverlayModule, SharedUIModule, TranslateModule, UserTeamComponent],
})
export class HeaderBarServicesComponent implements OnInit {
  private static readonly searchDebounceMs = 150;

  // Latest real "all eligible services enabled" state, used to restore the toggle after a failure.
  private latestAreAllEnabled = false;

  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;
  public readonly UserAssignmentType = UserAssignmentType;
  public readonly UserTeamVariant = UserTeamVariant;
  public readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];

  public readonly isOpen$ = new BehaviorSubject<boolean>(false);
  public readonly searchControl = new FormControl<string>('', { nonNullable: true });
  // Drives the All Services toggle; a FormControl lets us force the switch back to the real
  // membership state after a failed bulk update (the wrapper's own value would otherwise stick).
  public readonly allServicesControl = new FormControl<boolean>(false, { nonNullable: true });
  // Prevents the one-way All Services toggle from showing stale state while setAll() is in flight.
  public readonly isSettingAll$ = new BehaviorSubject<boolean>(false);

  public teams$: Observable<IUserMembership[]>;
  public filteredTeams$: Observable<IUserMembership[]>;
  public filteredTeamsBased$: Observable<IUserMembership[]>;
  public filteredContactCenter$: Observable<IUserMembership[]>;
  public hasTeamsBased$: Observable<boolean>;
  public hasContactCenter$: Observable<boolean>;
  public isEmpty$: Observable<boolean>;
  public toggleUserEnabilityTeamIds$: Observable<Set<string>>;
  public enabledCount$: Observable<number>;
  public totalCount$: Observable<number>;
  public queuedTotal$: Observable<number>;
  public isOnDuty$: Observable<boolean>;
  public activeProfile$: Observable<IUserResponsibilityProfile | null>;
  public eligibleTeams$: Observable<IUserMembership[]>;
  public hasEligibleTeams$: Observable<boolean>;
  public areAllEnabled$: Observable<boolean>;
  public isMixed$: Observable<boolean>;
  public isOnAttendantConsole$: Observable<boolean>;
  public pinnedTeamIds$: Observable<Set<string>>;
  public canPinMore$: Observable<boolean>;

  public constructor(
    private readonly userHeaderStateService: UserHeaderStateService,
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly reportingServiceSessionsService: ReportingServiceSessionsService,
    private readonly memberService: MemberService,
    private readonly authenticationService: IAuthenticationService,
    private readonly responsibilityProfileService: ResponsibilityProfileService,
    private readonly headerBarNavigationService: HeaderBarNavigationService,
    private readonly pinnedServicesService: HeaderBarPinnedServicesService,
    private readonly toastrService: ToastrService,
    private readonly translateService: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.responsibilityProfileService.initialize();
    const { userId } = this.authenticationService.getUserInfo();
    this.isOnAttendantConsole$ = this.headerBarNavigationService.isOnAttendantConsole$;
    this.pinnedTeamIds$ = this.pinnedServicesService.pinnedTeamIds$.pipe(map((ids) => new Set(ids)));
    this.canPinMore$ = this.pinnedServicesService.canPinMore$;
    this.teams$ = this.userHeaderStateService.teams$.pipe(
      // A Direct Call Service proxy team is membership-visible to more than just its owner, so
      // restrict it to the owning agent before pinning/sorting (mirrors legacy header).
      map((items) =>
        items.filter((x) => !x.teamContactInfo?.isDirectCallService || x.teamContactInfo?.directCallOwningUserO365Id === userId),
      ),
      // Drop stale pins whenever the agent's memberships change.
      tap((items) => this.pinnedServicesService.prune(new Set(items.map((item) => item.teamId)))),
      map((items) => sortBy(items, (x) => x.teamContactInfo?.serviceName ?? x.teamContactInfo?.name)),
    );
    this.toggleUserEnabilityTeamIds$ = this.permissionManagerService
      .getTeamIds$(NimbusPermission.ToggleUserEnability)
      .pipe(map((ids) => new Set(ids)));

    this.eligibleTeams$ = combineLatest([this.teams$, this.toggleUserEnabilityTeamIds$]).pipe(
      map(([teams, ids]) => teams.filter((team) => this.isToggleable(team, ids))),
    );
    this.hasEligibleTeams$ = this.eligibleTeams$.pipe(map((teams) => teams.length > 0));
    this.areAllEnabled$ = this.eligibleTeams$.pipe(map((teams) => teams.length > 0 && teams.every((team) => team.enabled)));
    // Keep the toggle in sync with the real membership state and remember it for post-failure restore.
    this.areAllEnabled$.pipe(untilDestroyed(this)).subscribe((areAllEnabled) => {
      this.latestAreAllEnabled = areAllEnabled;
      this.allServicesControl.setValue(areAllEnabled, { emitEvent: false });
    });
    // Mixed only considers teams that actually expose the bulk toggle.
    this.isMixed$ = this.eligibleTeams$.pipe(map((teams) => teams.some((team) => team.enabled) && !teams.every((team) => team.enabled)));

    this.hasTeamsBased$ = this.teams$.pipe(map((teams) => teams.some((team) => this.isTeamsBased(team))));
    this.hasContactCenter$ = this.teams$.pipe(
      map((teams) => teams.some((team) => team.teamContactInfo?.userAssignmentType === UserAssignmentType.SkillBased)),
    );
    this.isEmpty$ = this.teams$.pipe(map((teams) => teams.length === 0));

    this.filteredTeams$ = combineLatest([
      this.teams$,
      this.searchControl.valueChanges.pipe(startWith(''), debounceTime(HeaderBarServicesComponent.searchDebounceMs)),
    ]).pipe(
      map(([teams, search]) => {
        const term = search.trim().toLowerCase();
        return term
          ? teams.filter((team) => (team.teamContactInfo?.serviceName || team.teamContactInfo?.name || '').toLowerCase().includes(term))
          : teams;
      }),
    );
    this.filteredTeamsBased$ = this.filteredTeams$.pipe(map((teams) => teams.filter((team) => this.isTeamsBased(team))));
    this.filteredContactCenter$ = this.filteredTeams$.pipe(
      map((teams) => teams.filter((team) => team.teamContactInfo?.userAssignmentType === UserAssignmentType.SkillBased)),
    );

    this.isOnDuty$ = this.responsibilityProfileService.activeProfile$.pipe(map((profile) => profile?.isDuty ?? true));
    this.activeProfile$ = this.responsibilityProfileService.activeProfile$;
    this.enabledCount$ = combineLatest([this.teams$, this.responsibilityProfileService.activeProfile$]).pipe(
      map(([teams, activeProfile]) => teams.filter((team) => this.isCounted(team, activeProfile)).length),
    );
    this.totalCount$ = this.teams$.pipe(map((teams) => teams.length));
    this.queuedTotal$ = this.teams$.pipe(
      switchMap((teams) =>
        teams.length
          ? this.reportingServiceSessionsService
              // Query every team at once so distribution-profile sessions shared across teams are counted only once.
              .getServiceSessions$(
                teams.map((team) => team.teamId),
                [CombinedConversationState.InQueue],
              )
              .pipe(map((sessions) => sessions.length))
          : of(0),
      ),
    );
  }

  public togglePanel(): void {
    this.isOpen$.next(!this.isOpen$.getValue());
    if (!this.isOpen$.getValue()) {
      this.searchControl.setValue('');
    }
  }

  public closePanel(): void {
    this.isOpen$.next(false);
    this.searchControl.setValue('');
  }

  public onTeamClick(team: IUserMembership): void {
    this.closePanel();
    this.headerBarNavigationService.openTeamInAttendantConsole(team.teamId);
  }

  public togglePin(team: IUserMembership, event: Event): void {
    // Keep the row click (AC navigation) from firing when pinning.
    event.stopPropagation();
    this.pinnedServicesService.toggle(team.teamId);
  }

  public setAll(state: boolean): void {
    const { userId } = this.authenticationService.getUserInfo();
    const startedAt = moment().toISOString();

    this.isSettingAll$.next(true);
    combineLatest([this.teams$, this.toggleUserEnabilityTeamIds$])
      .pipe(
        // Snapshot only; otherwise later teams$ emissions re-fire this bulk toggle.
        take(1),
        map(([teams, toggleUserEnabilityTeamIds]) =>
          teams
            .filter((team) => this.isToggleable(team, toggleUserEnabilityTeamIds) && team.enabled !== state)
            .map((team) => ({ team, withCheck: !toggleUserEnabilityTeamIds.has(team.teamId) })),
        ),
        switchMap((eligibleTeams) =>
          eligibleTeams.length
            ? forkJoin(
                eligibleTeams.map(({ team, withCheck }) =>
                  this.memberService.setActiveState(team.teamId, userId, state, startedAt, withCheck).pipe(
                    map(() => true),
                    // Isolate per-team failures so one rejected request does not abort the whole bulk toggle.
                    catchError(() => of(false)),
                  ),
                ),
              )
            : of<boolean[]>([]),
        ),
        // Surface a partial bulk failure instead of letting the error go unhandled.
        tap((results) => {
          if (results.some((succeeded) => !succeeded)) {
            this.toastrService.error(this.translateService.instant(TranslationKeys.HeaderBarAllServicesError));
            // The wrapper already moved the switch to the requested state; areAllEnabled$ will not
            // re-emit after a failed update, so restore the control from the real membership state.
            this.allServicesControl.setValue(this.latestAreAllEnabled, { emitEvent: false });
          }
        }),
        finalize(() => this.isSettingAll$.next(false)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  public isToggleable(team: IUserMembership, toggleUserEnabilityTeamIds: Set<string>): boolean {
    // Must mirror the per-row toggle visibility (UserTeamComponent.hasActiveStateToggle + the template guard).
    return this.isTeamsBased(team) && (toggleUserEnabilityTeamIds.has(team.teamId) || !!team.teamContactInfo?.isMemberCanChangeActive);
  }

  public isCounted(team: IUserMembership, activeProfile: IUserResponsibilityProfile | null): boolean {
    // Skill-based services have no toggle; duty state defines whether they count as active.
    if (team.teamContactInfo?.userAssignmentType === UserAssignmentType.SkillBased) {
      return activeProfile ? activeProfile.isDuty : true;
    }
    return team.enabled;
  }

  // Teams-based group covers Microsoft Teams-based services and the owner's Direct Call service (both expose the active toggle).
  private isTeamsBased(team: IUserMembership): boolean {
    const assignmentType = team.teamContactInfo?.userAssignmentType;
    return assignmentType === UserAssignmentType.MicrosoftTeamsBased || assignmentType === UserAssignmentType.DirectCall;
  }
}
